import React, { useEffect, useState, useCallback, memo, useRef } from 'react';
import { Picker, Item, View, Text } from '@adobe/react-spectrum';
import { FormField } from '../../store/slice';
import { API_ENDPOINTS, fetchDropdownOptions } from '../../store/api';
import { getCookie } from '../../../../../utils/helpers';
import styles from '../FormField.module.scss';
import { fetchEventsCached } from '../../../../services/momentsApi';
import { productHierarchyService } from '../../services/productHierarchyService';
import { useEventKeyDatePermissions } from '../../../../utils/rbacHandler';
import { isLegacyOfferOrPromoMomentOption } from '../../utils/legacyOfferPromoMomentType';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';

interface OptionItem {
  value: string;
  label: string;
}

interface DropdownComponentProps {
  field: any;
  value: any;
  onChange: (value: any) => void;
  formValues: Record<string, any>;
  onFocus?: () => void;
  validationState?: 'valid' | 'invalid';
  errorMessage?: string;
  disabled?: boolean;
}

// Cache for dropdown options to prevent unnecessary API calls
const dropdownOptionsCache: Record<string, OptionItem[]> = {};

/**
 * Component for rendering dropdown fields (select and api_dropdown)
 */
export const DropdownComponent: React.FC<DropdownComponentProps> = memo(
  ({
    field,
    value,
    onChange,
    formValues,
    onFocus,
    validationState,
    errorMessage,
    disabled
  }) => {
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Get admin permissions for filtering moment type options
    const { isAdmin, canCreateEvents, canCreateKeyDates } =
      useEventKeyDatePermissions();

    // Helper function to filter moment type options based on admin permissions
    const filterMomentTypeOptions = (options: OptionItem[]): OptionItem[] => {
      if (field.name !== 'momentType') return options;

      return options
        .filter(option => {
          if (!isAdmin) {
            const label = option.label.toLowerCase();
            if (label.includes('key date') || label.includes('event')) {
              return false;
            }
          }
          return true;
        })
        .filter(option => !isLegacyOfferOrPromoMomentOption(option));
    };

    // Fetch options from API if needed
    useEffect(() => {
      const fetchOptionsFromApi = async () => {
        // Only fetch if this has an API endpoint
        if (field.type !== 'api_dropdown' || !field.api) {
          return;
        }

        // Skip cache for categories field and events endpoint
        const skipCache =
          field.name === 'categories' ||
          field.name === 'products' ||
          field.name === 'customerAudiences' ||
          field.api === 'moments/type/event';
        const cacheKey =
          field.api === 'product-hierarchy-filters'
            ? `${field.api}:${field.name}`
            : field.api;
        if (!skipCache && dropdownOptionsCache[cacheKey]) {
          const filteredOptions = filterMomentTypeOptions(
            dropdownOptionsCache[cacheKey]
          );
          setOptions(filteredOptions);
          setIsLoading(false);
          return;
        }

        // For fields that depend on another field's value, check if we have that value
        if (field.dependsOn && formValues) {
          // Handle both string and array dependencies
          const dependencies = Array.isArray(field.dependsOn)
            ? field.dependsOn
            : [field.dependsOn];

          // Check if all dependencies are met (treat empty arrays/strings as missing)
          const isEmpty = (v: any) =>
            v === undefined ||
            v === null ||
            (Array.isArray(v) && v.length === 0) ||
            (typeof v === 'string' && v.trim() === '');
          const missingDependency = dependencies.some(dep =>
            isEmpty(formValues[dep])
          );
          if (missingDependency) {
            setOptions([]);
            // Clear selection when dependency becomes empty
            if (value) {
              onChange(undefined);
            }
            setIsLoading(false);
            return;
          }
        }

        setIsLoading(true);
        setError(null);

        try {
          // Special handling for product-hierarchy-filters endpoint
          if (field.api === 'product-hierarchy-filters') {
            try {
              let items: string[] = [];
              if (field.name === 'customerAudiences') {
                items = await productHierarchyService.fetchAllAudiences();
              } else {
                items = await productHierarchyService.fetchProducts();
              }

              const mappedOptions = items.map((item: string) => ({
                label: item,
                value: item
              }));

              dropdownOptionsCache[`${field.api}:${field.name}`] =
                mappedOptions;
              const filteredOptions = filterMomentTypeOptions(mappedOptions);
              setOptions(filteredOptions);
              setIsLoading(false);
              return;
            } catch (error) {
              setError(
                `Failed to load options: ${
                  error instanceof Error ? error.message : 'Unknown error'
                }`
              );
              setIsLoading(false);
              return;
            }
          }

          // Special handling for events endpoint - use centralized service to avoid duplicate API calls
          if (field.api === 'moments/type/event') {
            const response = await fetchEventsCached();

            if (response.success && response.data) {
              const formattedOptions = response.data.map((event: any) => ({
                value: event.id,
                label: event.name
              }));
              const filteredOptions = filterMomentTypeOptions(formattedOptions);
              setOptions(filteredOptions);
            } else {
              console.error(
                '❌ DropdownComponent: Failed to load events:',
                response.error
              );
              setError(response.message || 'Failed to load events');
              setOptions([]);
            }
            setIsLoading(false);
            return;
          }

          // Default handling for other APIs
          let url = field.api;

          // If the URL is not absolute, prefix with the v2 API base URL
          if (!url.startsWith('http')) {
            const baseUrl = process.env.REACT_APP_API_V2_URL;
            if (!baseUrl) {
              setError(
                'API base URL is not configured. Please set REACT_APP_API_V2_URL in your environment.'
              );
              setIsLoading(false);
              return;
            }
            url = `${baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
          }

          // If this field depends on another field, append the selected value as a query parameter
          if (field.dependsOn && formValues) {
            const dependencies = Array.isArray(field.dependsOn)
              ? field.dependsOn
              : [field.dependsOn];

            dependencies.forEach(dep => {
              const dependencyValue = formValues[dep];
              if (dependencyValue) {
                // If the URL contains a placeholder, replace it
                if (url.includes(`{${dep}}`)) {
                  url = url.replace(`{${dep}}`, dependencyValue);
                }
              }
            });
          }

          // Get the auth token from cookies if available
          const token = getCookie('app_access_token');
          const headers: HeadersInit = {
            'Content-Type': 'application/json'
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          } else {
            console.warn('No bearer token found in cookies for API request');
          }

          const response = await fetch(url, {
            method: 'GET',
            headers
          });

          if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
          }

          // Check content type to ensure we have JSON
          const contentType = response.headers.get('content-type');
          if (contentType && !contentType.includes('json')) {
            const text = await response.text();
            throw new Error(`API returned non-JSON response (${contentType})`);
          }

          let data;
          try {
            data = await response.json();
          } catch (error: any) {
            const text = await response.text();
            throw new Error(
              `Failed to parse JSON response: ${
                error.message || 'Unknown parsing error'
              }`
            );
          }

          // Handle different response formats
          let items: any[] = [];

          if (Array.isArray(data)) {
            items = data;
          } else if (data && typeof data === 'object') {
            // Try to find an array property in the response
            const arrayProps = Object.keys(data).filter(key =>
              Array.isArray(data[key])
            );
            if (arrayProps.length > 0) {
              // Use the first array property found (prefer 'items' if it exists)
              const arrayProp = arrayProps.includes('items')
                ? 'items'
                : arrayProps[0];
              items = data[arrayProp];
            }
          }

          if (!items || !items.length) {
            setOptions([]);
            setIsLoading(false);
            return;
          }

          // Map the response to the expected format for the dropdown
          const mappedOptions = items.map((item: any) => ({
            label: item.label || item.name || item.value || item.id || '',
            value: item.value || item.id || item.label || item.name || ''
          }));

          // Apply admin filtering for moment type options
          const filteredOptions = filterMomentTypeOptions(mappedOptions);

          // Only cache non-skip fields (categories, products, etc. are always re-fetched)
          if (!skipCache) {
            dropdownOptionsCache[field.api] = mappedOptions;
          }
          setOptions(filteredOptions);
        } catch (error) {
          setError(
            `Failed to load options: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          );
        }
        setIsLoading(false);
      };

      fetchOptionsFromApi();
    }, [field, formValues, isAdmin]);

    // Set default value when options are loaded and no value is set
    useEffect(() => {
      if (field.defaultValue && !value && options.length > 0) {
        // Find the option that matches the default value
        const defaultOption = options.find(
          opt =>
            opt.value === field.defaultValue || opt.label === field.defaultValue
        );

        if (defaultOption) {
          // Handle formatted values if needed
          if (field.valueFormat && defaultOption) {
            const formatted: Record<string, any> = {};
            Object.keys(field.valueFormat).forEach(typeKey => {
              if (defaultOption[typeKey] !== undefined) {
                formatted[typeKey] = defaultOption[typeKey];
              } else if (
                typeKey === 'id' &&
                (defaultOption as any).value !== undefined
              ) {
                formatted[typeKey] = (defaultOption as any).value;
              } else if (
                typeKey === 'value' &&
                (defaultOption as any).id !== undefined
              ) {
                formatted[typeKey] = (defaultOption as any).id;
              } else if (
                typeKey === 'name' &&
                defaultOption.label !== undefined
              ) {
                formatted[typeKey] = (defaultOption as any).label;
              } else if (
                typeKey === 'label' &&
                (defaultOption as any).name !== undefined
              ) {
                formatted[typeKey] = (defaultOption as any).name;
              }
            });
            onChange(formatted);
          } else {
            onChange(defaultOption.value);
          }
        }
      }
    }, [field.defaultValue, value, options, onChange, field.valueFormat]);

    // If we have predefined options, use those instead of API
    useEffect(() => {
      if (field.type === 'select' && field.options) {
        // Map static options to { label, value }
        const mappedOptions = field.options.map((item: any) => ({
          label: item.label || item.name || item.value || item.id || '',
          value: item.value || item.id || item.label || item.name || ''
        }));
        const filteredOptions = filterMomentTypeOptions(mappedOptions);
        setOptions(filteredOptions);
      }
    }, [field.type, field.options, isAdmin]);

    // Ensure that selectedKey is either a string or undefined
    let selectedKey;
    if (field.valueFormat && value && typeof value === 'object') {
      selectedKey = value.id || value.value || undefined;
    } else if (value === '' || value === null || value === false) {
      selectedKey = undefined;
    } else if (value) {
      selectedKey = value.toString();
    } else {
      selectedKey = undefined;
    }

    // Custom placeholder for "WHY ARE WE DOING THIS" field
    const getPlaceholder = () => {
      if (field.label === 'WHY ARE WE DOING THIS') {
        return 'Select Primary';
      }
      return field.placeholder || '';
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const handleOptionClick = (optionValue: string) => {
      const selectedObj = options.find(opt => opt.value === optionValue);
      if (field.valueFormat && selectedObj) {
        const formatted: Record<string, any> = {};
        Object.keys(field.valueFormat).forEach(typeKey => {
          if (selectedObj[typeKey] !== undefined) {
            formatted[typeKey] = selectedObj[typeKey];
          } else if (
            typeKey === 'id' &&
            (selectedObj as any).value !== undefined
          ) {
            formatted[typeKey] = (selectedObj as any).value;
          } else if (
            typeKey === 'value' &&
            (selectedObj as any).id !== undefined
          ) {
            formatted[typeKey] = (selectedObj as any).id;
          } else if (typeKey === 'name' && selectedObj.label !== undefined) {
            formatted[typeKey] = (selectedObj as any).label;
          } else if (
            typeKey === 'label' &&
            (selectedObj as any).name !== undefined
          ) {
            formatted[typeKey] = (selectedObj as any).name;
          }
        });
        onChange(formatted);
      } else {
        onChange(optionValue);
      }
      setIsOpen(false);
    };

    const getSelectedLabel = () => {
      const selected = options.find(opt => opt.value === selectedKey);
      return selected ? selected.label : getPlaceholder();
    };

    return (
      <>
        <div className={styles.fieldLabel}>
          {field.label}
          {field.required && <span className={styles.required}> *</span>}
        </div>

        <div className={styles.customDropdown} ref={dropdownRef}>
          <button
            type="button"
            className={styles.dropdownButton}
            onClick={() =>
              !field.disabled && !isLoading && !disabled && setIsOpen(!isOpen)
            }
            onFocus={onFocus}
            disabled={field.disabled || isLoading || disabled}
          >
            <span className={!selectedKey ? styles.placeholder : ''}>
              {getSelectedLabel()}
            </span>
            <ChevronDown />
          </button>

          {isOpen && (
            <div className={styles.dropdownList}>
              {options.map(option => (
                <div
                  key={option.value}
                  className={`${styles.dropdownItem} ${
                    selectedKey === option.value ? styles.selected : ''
                  }`}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <div className={styles.optionLabel}>
                    <span>{option.label}</span>
                    {selectedKey === option.value && (
                      <BlackCheckMarkIcon className={styles.checkmarkIcon} />
                    )}
                  </div>
                </div>
              ))}
              {options.length === 0 && !isLoading && (
                <div className={styles.dropdownItem}>No options available</div>
              )}
            </div>
          )}
        </div>

        {error && <Text UNSAFE_className={styles.errorText}>{error}</Text>}
      </>
    );
  },
  // Memoization comparison function
  (prevProps, nextProps) => {
    // Always re-render if the field configuration changes
    if (prevProps.field !== nextProps.field) {
      return false; // Re-render
    }

    // Always re-render if the value changes
    if (prevProps.value !== nextProps.value) {
      return false; // Re-render
    }

    // For dependent dropdowns, check if any dependency has changed
    if (
      prevProps.field.dependsOn &&
      prevProps.formValues &&
      nextProps.formValues
    ) {
      const dependencies = Array.isArray(prevProps.field.dependsOn)
        ? prevProps.field.dependsOn
        : [prevProps.field.dependsOn];

      const hasChangedDependency = dependencies.some(dep => {
        const prevDependency = prevProps.formValues[dep];
        const nextDependency = nextProps.formValues[dep];
        return prevDependency !== nextDependency;
      });

      if (hasChangedDependency) {
        return false; // Re-render if any dependency changed
      }
    }

    // Re-render when validation props change
    if (
      prevProps.validationState !== nextProps.validationState ||
      prevProps.errorMessage !== nextProps.errorMessage
    ) {
      return false;
    }

    // By default, skip re-render
    return true;
  }
);
